// src/components/resources/calculators/LTVCalculator.tsx

"use client"

import { useMemo, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import RiMailLine from "~/icons/remix/generated/RiMailLine"
import { FAQ } from "~/components/shared/FAQ"
import { createEngagementTimers, sendResourceEvent } from "~/lib/gtag"
import { debounce } from "~/lib/utils/events"
import {
  calculateMetrics,
  type CalculatorInputs,
  type CalculatorResults,
} from "./LTVCalculator.logic"

const FAQ_ITEMS = [
  {
    question: "What is Customer Lifetime Value (LTV)?",
    answer:
      "Customer Lifetime Value (LTV) is the total revenue a business can expect from a customer throughout their entire relationship. It helps businesses understand how much they can spend to acquire customers while remaining profitable. LTV is a forward-looking metric that predicts future revenue streams and helps prioritize customer segments based on their long-term value.",
  },
  {
    question: "How is LTV calculated?",
    answer:
      "LTV is calculated by multiplying average purchase value by purchase frequency and an effective customer lifespan. In this calculator, annual churn derives that lifespan (1 ÷ churn rate), with the planning horizon used only when churn is 0%. This gives you gross LTV, which is adjusted for profit margin and acquisition cost to get net LTV.",
  },
  {
    question: "Why is LTV important?",
    answer:
      "LTV helps businesses understand the long-term value of their customer relationships, guides customer acquisition spending, and identifies opportunities for increasing customer retention and revenue per customer. It's essential for sustainable growth because it helps you determine how much you can afford to spend on acquiring new customers while maintaining profitability. LTV also helps prioritize marketing efforts toward high-value customer segments.",
  },
  {
    question: "What's a good LTV to CAC ratio?",
    answer:
      "A healthy LTV to CAC (Customer Acquisition Cost) ratio is typically 3:1 or higher. This means you earn at least 3 times what you spend to acquire a customer, ensuring sustainable growth. For SaaS businesses, a 3:1 ratio is considered the minimum viable ratio, while e-commerce businesses might aim for 4:1 or higher. Early-stage startups might temporarily operate at lower ratios while establishing market position.",
  },
  {
    question: "How can I improve customer LTV?",
    answer:
      "You can improve LTV by increasing purchase frequency (through engagement and retention), average purchase value (through upselling and cross-selling), or customer lifespan (through better customer service and loyalty programs). Specific strategies include implementing subscription models, creating loyalty programs, personalizing customer experiences, improving product quality, and developing effective onboarding processes to ensure customers get maximum value from your products or services.",
  },
  {
    question: "How does churn rate affect LTV?",
    answer:
      "Churn rate directly impacts LTV by determining the effective customer lifespan. A high annual churn rate means customers leave sooner, reducing their lifetime value. For example, reducing annual churn from 20% to 10% increases the modeled average lifespan from 5 years to 10 years, doubling the revenue window before margin and acquisition-cost adjustments.",
  },
  {
    question: "Should LTV calculations vary by customer segment?",
    answer:
      "Yes, LTV should be calculated separately for different customer segments whenever possible. Different customer groups often have vastly different purchasing behaviors, retention rates, and profitability. Segmenting your LTV analysis by acquisition channel, demographics, product usage patterns, or customer size can reveal which segments deliver the highest return and deserve more investment.",
  },
  {
    question: "How often should I recalculate LTV?",
    answer:
      "LTV should be recalculated regularly as your business evolves. For established businesses with stable customer behavior, quarterly updates may be sufficient. For rapidly growing companies or those with seasonal fluctuations, monthly recalculations are recommended. After major product changes or pricing updates, it's important to reassess LTV to understand the impact on customer value.",
  },
  {
    question: "What is a good CAC payback period?",
    answer:
      "CAC payback — the months of gross margin needed to recover acquisition cost — matters as much as the LTV:CAC ratio for cash-constrained businesses, because a 3:1 ratio that takes 30 months to recoup ties up far more cash than a 3:1 with a 9-month payback. Typical 2025 targets are under 12 months for SMB, under 18 months for mid-market, and under 24 months for enterprise; scale-stage companies above $10M ARR often reach 80–120 day paybacks. Track payback alongside LTV:CAC so a healthy-looking ratio doesn't mask a cash-flow problem.",
  },
]

export function LTVCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    averagePurchaseValue: 100,
    purchaseFrequency: 4,
    customerLifespan: 3,
    churnRate: 20,
    acquisitionCost: 50,
    profitMargin: 30,
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [email, setEmail] = useState("")
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])
  const previewMetrics = useMemo(() => calculateMetrics(inputs), [inputs])
  const previewChurnRate = 100 - previewMetrics.retentionRate
  const usesFallbackLifespan = previewChurnRate === 0

  const _debouncedInteract = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const interactionType = (args[0] as string) ?? "input_change"
        sendResourceEvent("interact", {
          resourceType: "calculator",
          resourceSlug: "customer-ltv-calculator",
          engagementMs: getDeltaMs(),
          params: { interaction_type: interactionType },
        })
      }, 400),
    [getDeltaMs],
  )

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const handleCalculate = (): void => {
    setResults(calculateMetrics(inputs))
    setShowEmailCapture(true)
    sendResourceEvent("interact", {
      resourceType: "calculator",
      resourceSlug: "customer-ltv-calculator",
      engagementMs: getDeltaMs(),
      params: { interaction_type: "calculate" },
    })
    sendResourceEvent("complete", {
      resourceType: "calculator",
      resourceSlug: "customer-ltv-calculator",
      engagementMs: getTotalMs(),
      params: {},
    })
  }

  return (
    <div
      className="mx-auto max-w-4xl"
      role="main"
      aria-labelledby="calculator-heading"
    >
      <div className="rounded-2xl shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2
            id="calculator-heading"
            className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white"
          >
            Customer Lifetime Value Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate your customer lifetime value and get insights to improve
            retention
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column - Revenue Metrics */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Revenue Metrics
            </h3>
            <div>
              <label
                htmlFor="average-value"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Average Purchase Value ($)
              </label>
              <input
                id="average-value"
                type="number"
                step="0.01"
                min="0"
                value={inputs.averagePurchaseValue}
                onChange={(e) => {
                  setInputs({
                    ...inputs,
                    averagePurchaseValue: +e.target.value,
                  })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div>
              <label
                htmlFor="purchase-frequency"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Purchase Frequency (per year)
              </label>
              <input
                id="purchase-frequency"
                type="number"
                step="0.1"
                min="0"
                value={inputs.purchaseFrequency}
                onChange={(e) => {
                  setInputs({ ...inputs, purchaseFrequency: +e.target.value })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div>
              <label
                htmlFor="profit-margin"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Profit Margin (%)
              </label>
              <input
                id="profit-margin"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={inputs.profitMargin}
                onChange={(e) => {
                  setInputs({ ...inputs, profitMargin: +e.target.value })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>
          </div>

          {/* Right Column - Customer Metrics */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Customer Metrics
            </h3>
            <div>
              <label
                htmlFor="customer-lifespan"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Fallback Lifespan if Churn is 0% (years)
              </label>
              <input
                id="customer-lifespan"
                type="number"
                step="0.1"
                min="0"
                value={inputs.customerLifespan}
                onChange={(e) => {
                  setInputs({ ...inputs, customerLifespan: +e.target.value })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Annual churn derives lifespan when it is above 0%; this horizon
                is used only for a 0% churn scenario.
              </p>
            </div>

            <div>
              <label
                htmlFor="churn-rate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Annual Churn Rate (%)
              </label>
              <input
                id="churn-rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={inputs.churnRate}
                onChange={(e) => {
                  setInputs({ ...inputs, churnRate: +e.target.value })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
              <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
                {usesFallbackLifespan
                  ? `Using fallback horizon: ${previewMetrics.effectiveCustomerLifespan.toFixed(1)} years (${previewChurnRate}% churn).`
                  : `Current model: ${previewMetrics.effectiveCustomerLifespan.toFixed(1)} year lifespan from ${previewChurnRate}% annual churn.`}
              </p>
            </div>

            <div>
              <label
                htmlFor="acquisition-cost"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Customer Acquisition Cost ($)
              </label>
              <input
                id="acquisition-cost"
                type="number"
                step="0.01"
                min="0"
                value={inputs.acquisitionCost}
                onChange={(e) => {
                  setInputs({ ...inputs, acquisitionCost: +e.target.value })
                  trackInteract("input_change")
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate lifetime value metrics"
          >
            Calculate LTV
          </button>
        </div>
      </div>

      {/* Results Section - conditionally rendered */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8"
          >
            {/* Combined Metrics Card */}
            <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
              {/* Customer Value Metrics */}
              <div className="mb-10">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Customer Value Metrics
                </h3>
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Net LTV
                    </dt>
                    <dd className="mt-2 text-3xl font-semibold text-indigo-600 dark:text-indigo-400">
                      ${Math.round(results.netLTV).toLocaleString()}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      ROI
                    </dt>
                    <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                      {Number.isFinite(results.roi)
                        ? `${Math.round(results.roi)}%`
                        : "N/A"}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Months to ROI
                    </dt>
                    <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                      {Math.round(results.monthsToROI)}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Modeled Lifespan
                    </dt>
                    <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                      {results.effectiveCustomerLifespan.toFixed(1)}y
                    </dd>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {results.retentionRate}% annual retention
                    </p>
                  </div>
                </dl>
              </div>

              {/* Revenue Breakdown */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Revenue Breakdown
                </h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Monthly Revenue
                    </dt>
                    <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                      ${Math.round(results.monthlyRevenue).toLocaleString()}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Annual Revenue
                    </dt>
                    <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                      ${Math.round(results.annualRevenue).toLocaleString()}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Lifetime Revenue
                    </dt>
                    <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                      ${Math.round(results.lifetimeRevenue).toLocaleString()}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Email Capture */}
            {showEmailCapture && (
              <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
                <div className="flex items-center gap-4">
                  <RiMailLine className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Get a Detailed Analysis
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Enter your email to receive a detailed breakdown of your
                      LTV metrics and personalized recommendations.
                    </p>
                  </div>
                </div>
                <div className="mt-4 sm:flex sm:items-center sm:gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 sm:text-sm"
                  />
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:mt-0 sm:w-auto"
                  >
                    Send Analysis
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section - always visible */}
      <div className="mt-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ
          items={FAQ_ITEMS}
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "xl" }}
          className="w-full"
        />
      </div>
    </div>
  )
}
